import { useMsal } from '@azure/msal-react';
import { apiConfig } from '../config/msalConfig';
import { useCallback, useMemo, useRef } from 'react';
import { Project, Epic, Session, User, Assignment } from '../types';

export interface ApiClient {
    // Projects
    getProjects: () => Promise<Project[]>;
    createProject: (data: Partial<Project>) => Promise<Project>;
    updateProject: (id: string, data: Partial<Project>) => Promise<Project>;
    deleteProject: (id: string) => Promise<void>;

    // Epics
    getEpics: (projectId?: string) => Promise<Epic[]>;
    createEpic: (data: Partial<Epic>) => Promise<Epic>;
    updateEpic: (id: string, data: Partial<Epic>) => Promise<Epic>;
    deleteEpic: (id: string) => Promise<void>;

    // Sessions
    getSessions: () => Promise<Session[]>;
    createSession: (data: Partial<Session>) => Promise<Session>;

    // Users (Admin)
    getUsers: () => Promise<User[]>;
    createUser: (data: Partial<User>) => Promise<User>;
    getCurrentUser: () => Promise<User>;
    updateUser: (id: string, data: Partial<User>) => Promise<User>;
    deleteUser: (id: string) => Promise<void>;
    cleanupUnnamedUsers: () => Promise<{ deletedCount: number; message: string }>;

    // Assignments
    getAssignments: (userId?: string) => Promise<Assignment[]>;
    createAssignment: (data: { userId: string; projectId: string }) => Promise<Assignment>;
    deleteAssignment: (id: string) => Promise<void>;

    // Audit Logs (Admin)
    getAuditLogs: () => Promise<any[]>;

    // Analytics
    logAnalyticsEvent: (eventType: string, data?: any) => Promise<void>;
    getAnalyticsSummary: (days?: number, userEmail?: string) => Promise<any>;
    getAnalyticsEvents: (limit?: number) => Promise<any[]>;

    // Debug
    debug: () => Promise<any>;
}

/**
 * Request cache for deduplication.
 * Stores both in-flight promises AND recently completed results.
 */
interface CacheEntry {
    promise: Promise<any>;
    timestamp: number;
    result?: any;
    status: 'pending' | 'resolved' | 'rejected';
}

// Cache with 2-second cooldown for completed requests
const CACHE_COOLDOWN_MS = 2000;
const requestCache = new Map<string, CacheEntry>();

// Cleanup old cache entries periodically
setInterval(() => {
    const now = Date.now();
    for (const [key, entry] of requestCache.entries()) {
        if (entry.status !== 'pending' && now - entry.timestamp > CACHE_COOLDOWN_MS) {
            requestCache.delete(key);
        }
    }
}, 5000);

// Token acquisition lock to prevent multiple simultaneous popups
let tokenAcquisitionPromise: Promise<string> | null = null;

// API client with automatic token acquisition and request deduplication
export function useApi(): ApiClient {
    const { instance, accounts } = useMsal();

    // Track deduplication stats for debugging
    const statsRef = useRef({ deduped: 0, cached: 0, total: 0 });

    const getAccessToken = useCallback(async () => {
        if (accounts.length === 0) {
            throw new Error('No authenticated user');
        }

        // If there's already a token acquisition in progress, wait for it
        if (tokenAcquisitionPromise) {
            return tokenAcquisitionPromise;
        }

        try {
            const response = await instance.acquireTokenSilent({
                scopes: apiConfig.scopes,
                account: accounts[0],
            });
            return response.accessToken;
        } catch (error) {
            // Check if there's already an interaction in progress
            if ((error as any)?.errorCode === 'interaction_in_progress') {
                // Wait a bit and retry silently - the interaction should complete soon
                await new Promise(resolve => setTimeout(resolve, 1000));
                try {
                    const response = await instance.acquireTokenSilent({
                        scopes: apiConfig.scopes,
                        account: accounts[0],
                        forceRefresh: true,
                    });
                    return response.accessToken;
                } catch {
                    throw error; // Re-throw if still failing
                }
            }

            // Silent token acquisition failed, try interactive with lock
            tokenAcquisitionPromise = instance.acquireTokenPopup({
                scopes: apiConfig.scopes,
            }).then(response => {
                tokenAcquisitionPromise = null;
                return response.accessToken;
            }).catch(err => {
                tokenAcquisitionPromise = null;
                throw err;
            });

            return tokenAcquisitionPromise;
        }
    }, [instance, accounts]);

    const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}) => {
        const token = await getAccessToken();
        const url = `${apiConfig.baseUrl}${endpoint}`;

        // Build user principal header with MSAL account info
        const userPrincipal = {
            userId: accounts[0]?.localAccountId || 'unknown',
            identityProvider: 'aad',
            userDetails: accounts[0]?.username || '',
            userName: accounts[0]?.name || '', // Include display name for backend user creation
            userRoles: ['authenticated']
        };

        const encodedPrincipal = btoa(JSON.stringify(userPrincipal));

        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'x-user-identity': encodedPrincipal,
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(error.error || `HTTP ${response.status}`);
        }

        if (response.status === 204) {
            return null;
        }

        return response.json();
    }, [getAccessToken, accounts]);

    /**
     * Smart deduplication with cache cooldown.
     * - If request is in-flight, reuse the pending promise
     * - If request completed within cooldown period, return cached result
     * - Otherwise, make a new request
     */
    const fetchWithDedup = useCallback(async (endpoint: string, options: RequestInit = {}) => {
        const method = options.method?.toUpperCase() || 'GET';

        // Only deduplicate GET requests
        if (method !== 'GET') {
            return fetchWithAuth(endpoint, options);
        }

        const cacheKey = endpoint;
        const now = Date.now();

        statsRef.current.total++;

        // Check cache for in-flight or recently completed request
        const cached = requestCache.get(cacheKey);

        if (cached) {
            // If request is still pending, reuse the promise
            if (cached.status === 'pending') {
                statsRef.current.deduped++;
                // console.log(`[API Dedup] Reusing in-flight request for ${endpoint} (${statsRef.current.deduped}/${statsRef.current.total} deduped)`);
                return cached.promise;
            }

            // If request completed recently, return cached result
            if (cached.status === 'resolved' && now - cached.timestamp < CACHE_COOLDOWN_MS) {
                statsRef.current.cached++;
                // console.log(`[API Cache] Returning cached result for ${endpoint} (${statsRef.current.cached} cached hits)`);
                return Promise.resolve(cached.result);
            }
        }

        // Create new request with tracking
        const entry: CacheEntry = {
            promise: fetchWithAuth(endpoint, options).then(
                (result) => {
                    entry.status = 'resolved';
                    entry.result = result;
                    entry.timestamp = Date.now(); // Update timestamp on completion
                    return result;
                },
                (error) => {
                    entry.status = 'rejected';
                    entry.timestamp = Date.now();
                    requestCache.delete(cacheKey); // Don't cache errors
                    throw error;
                }
            ),
            timestamp: now,
            status: 'pending'
        };

        requestCache.set(cacheKey, entry);
        return entry.promise;
    }, [fetchWithAuth]);

    return useMemo(() => ({
        // Projects - GET uses dedup, mutations don't
        getProjects: () => fetchWithDedup('/projects'),
        createProject: (data) => fetchWithAuth('/projects', { method: 'POST', body: JSON.stringify(data) }),
        updateProject: (id, data) => fetchWithAuth(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteProject: (id) => fetchWithAuth(`/projects/${id}`, { method: 'DELETE' }),

        // Epics - GET uses dedup, mutations don't
        getEpics: (projectId) => fetchWithDedup(`/epics${projectId ? `?projectId=${projectId}` : ''}`),
        createEpic: (data) => fetchWithAuth('/epics', { method: 'POST', body: JSON.stringify(data) }),
        updateEpic: (id, data) => fetchWithAuth(`/epics/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteEpic: (id) => fetchWithAuth(`/epics/${id}`, { method: 'DELETE' }),

        // Sessions - GET uses dedup, mutations don't
        getSessions: () => fetchWithDedup('/sessions'),
        createSession: (data) => fetchWithAuth('/sessions', { method: 'POST', body: JSON.stringify(data) }),

        // Users - GET uses dedup, mutations don't
        getUsers: () => fetchWithDedup('/users'),
        createUser: (data) => fetchWithAuth('/users', { method: 'POST', body: JSON.stringify(data) }),
        getCurrentUser: () => fetchWithDedup('/users/me'),
        updateUser: (id, data) => fetchWithAuth(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
        deleteUser: (id) => fetchWithAuth(`/users/${id}`, { method: 'DELETE' }),
        cleanupUnnamedUsers: () => fetchWithAuth('/rpc/users/cleanup', { method: 'POST' }),

        // Assignments - GET uses dedup, mutations don't
        getAssignments: (userId) => fetchWithDedup(`/assignments${userId ? `?userId=${userId}` : ''}`),
        createAssignment: (data) => fetchWithAuth('/assignments', { method: 'POST', body: JSON.stringify(data) }),
        deleteAssignment: (id) => fetchWithAuth(`/assignments/${id}`, { method: 'DELETE' }),

        // Audit Logs - GET uses dedup
        getAuditLogs: () => fetchWithDedup('/audit-logs'),

        // Analytics
        logAnalyticsEvent: (eventType, data) => fetchWithAuth('/analytics/event', { method: 'POST', body: JSON.stringify({ eventType, data }) }),
        getAnalyticsSummary: (days = 7, userEmail) => fetchWithDedup(`/analytics/summary?days=${days}${userEmail ? `&userEmail=${encodeURIComponent(userEmail)}` : ''}`),
        getAnalyticsEvents: (limit = 50) => fetchWithDedup(`/analytics/events?limit=${limit}`),

        // Debug - GET uses dedup
        debug: () => fetchWithDedup('/debug'),
    }), [fetchWithDedup, fetchWithAuth]);
}
